'use client';

import { useResponsive } from '@larvitu/shared/react/hooks';
import { pxToRem, remToPx } from '@larvitu/shared/utils';
import { useTheme } from '@mui/material/styles';

import type { TypographyProps } from '@mui/material';
import type { Typography } from '@mui/material/styles/createTypography';
import type { Property } from 'csstype';

export interface UseGetFontValueProps {
  fontSize: number;
  lineHeight: number;
  fontWeight: Property.FontWeight | undefined;
  letterSpacing: Property.LetterSpacing<string | number> | undefined;
}

export const useGetFontValue = (
  variant: TypographyProps['variant']
): UseGetFontValueProps => {
  const theme = useTheme();
  const breakpoints = useWidth();

  const key = theme.breakpoints.up( breakpoints === 'xl' ? 'lg' : breakpoints );

  const hasResponsive = variant === 'h1'
    || variant === 'h2'
    || variant === 'h3'
    || variant === 'h4'
    || variant === 'h5'
    || variant === 'h6';

  const getFont: Typography = hasResponsive && theme.typography[variant][key]
    ? theme.typography[variant][key]
    : theme.typography[variant];

  const fontSize = remToPx( getFont.fontSize );
  const lineHeight = Number( theme.typography[variant].lineHeight ) * fontSize;
  const { fontWeight } = theme.typography[variant];
  const { letterSpacing } = theme.typography[variant];

  return {
    fontSize,
    lineHeight,
    fontWeight,
    letterSpacing,
  };
};

// -----------------------------------------------------------------------------

export const responsiveFontSizes = ({
  sm,
  md,
  lg,
}: {
  sm: number;
  md: number;
  lg: number;
}): Record<string, { fontSize: string }> => ({
  '@media (min-width:600px)': { fontSize: pxToRem( sm ) },
  '@media (min-width:900px)': { fontSize: pxToRem( md ) },
  '@media (min-width:1200px)': { fontSize: pxToRem( lg ) },
});

// -----------------------------------------------------------------------------

export const useWidth = (): string => {
  const theme = useTheme();
  const keys = [...theme.breakpoints.keys].reverse();

  return (
    // @ts-expect-error not sure what is this
    keys.reduce( (output, key) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const matches = useResponsive( 'up', key );

      return !output && matches ? key : output;
    }, null ) || 'xs'
  );
};
